import React, { useState, useEffect } from "react";
import { Patient } from "../../PatientCard";
import PatientOverviewService, {
  PatientOverviewData,
} from "../../../../services/PatientOverviewService";

interface PatientOverviewTabProps {
  patient: Patient;
  onDataUpdate?: (tabName: string) => void;
  refreshTrigger?: number;
}

const PatientOverviewTab: React.FC<PatientOverviewTabProps> = ({
  patient,
  onDataUpdate,
  refreshTrigger,
}) => {
  const [overviewData, setOverviewData] = useState<PatientOverviewData | null>(
    null
  );
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isMobile, setIsMobile] = useState(false);
  const [isTablet, setIsTablet] = useState(false);

  // Check screen size
  useEffect(() => {
    const checkScreenSize = () => {
      const width = window.innerWidth;
      setIsMobile(width < 768);
      setIsTablet(width >= 768 && width < 1024);
    };

    checkScreenSize();
    window.addEventListener("resize", checkScreenSize);
    return () => window.removeEventListener("resize", checkScreenSize);
  }, []);

  useEffect(() => {
    loadOverviewData();
  }, [patient.id, refreshTrigger]);

  const loadOverviewData = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const data = await PatientOverviewService.getPatientOverview(patient.id);
      setOverviewData(data);

      // Notify parent component that data has been updated
      if (onDataUpdate) {
        onDataUpdate("overview");
      }
    } catch (error) {
      console.error("Failed to load patient overview:", error);
      setError(
        error instanceof Error
          ? error.message
          : "Failed to load patient overview"
      );
    } finally {
      setIsLoading(false);
    }
  };

  if (isLoading) {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "400px",
          flexDirection: "column",
          gap: "16px",
          padding: isMobile ? "16px" : "24px",
        }}
      >
        <div
          style={{
            width: "40px",
            height: "40px",
            border: "4px solid #e2e8f0",
            borderTop: "4px solid #3b82f6",
            borderRadius: "50%",
            animation: "spin 1s linear infinite",
          }}
        />
        <p style={{ color: "#64748b", fontSize: isMobile ? "14px" : "16px" }}>
          Loading patient overview...
        </p>
      </div>
    );
  }

  if (error) {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "400px",
          flexDirection: "column",
          gap: "16px",
          padding: isMobile ? "16px" : "24px",
        }}
      >
        <div
          style={{
            color: "#dc2626",
            fontSize: isMobile ? "14px" : "16px",
            textAlign: "center",
          }}
        >
          <p>Failed to load patient overview</p>
          <p style={{ fontSize: "12px", marginTop: "8px" }}>{error}</p>
        </div>
        <button
          onClick={loadOverviewData}
          style={{
            padding: "8px 16px",
            backgroundColor: "#3b82f6",
            color: "white",
            border: "none",
            borderRadius: "6px",
            cursor: "pointer",
            fontSize: isMobile ? "14px" : "16px",
          }}
        >
          Try Again
        </button>
      </div>
    );
  }

  const containerStyle: React.CSSProperties = {
    padding: isMobile ? "16px" : isTablet ? "20px" : "24px",
    height: "100%",
    overflow: "auto",
    WebkitOverflowScrolling: "touch",
  };

  const getGridColumns = () => {
    if (isMobile) return "1fr";
    if (isTablet) return "repeat(2, 1fr)";
    return "repeat(auto-fit, minmax(300px, 1fr))";
  };

  const gridStyle: React.CSSProperties = {
    display: "grid",
    gridTemplateColumns: getGridColumns(),
    gap: isMobile ? "16px" : "24px",
    marginBottom: isMobile ? "16px" : "24px",
  };

  const cardStyle: React.CSSProperties = {
    background: "#ffffff",
    borderRadius: isMobile ? "8px" : "12px",
    padding: isMobile ? "16px" : "20px",
    boxShadow: "0 1px 3px rgba(0, 0, 0, 0.1)",
    border: "1px solid #e5e7eb",
  };

  const cardTitleStyle: React.CSSProperties = {
    fontSize: isMobile ? "16px" : "18px",
    fontWeight: "600",
    color: "#1f2937",
    marginBottom: isMobile ? "12px" : "16px",
    display: "flex",
    alignItems: "center",
    gap: isMobile ? "6px" : "8px",
    lineHeight: "1.2",
  };

  const alertStyle = (type: string): React.CSSProperties => {
    const styling = PatientOverviewService.getAlertStyling(type);
    return {
      padding: isMobile ? "10px 12px" : "12px 16px",
      borderRadius: "8px",
      marginBottom: "8px",
      fontSize: isMobile ? "12px" : "14px",
      lineHeight: "1.4",
      backgroundColor: styling.backgroundColor,
      border: `1px solid ${styling.borderColor}`,
      color: styling.color,
    };
  };

  const statItemStyle: React.CSSProperties = {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    padding: isMobile ? "10px 0" : "12px 0",
    borderBottom: "1px solid #f3f4f6",
    fontSize: isMobile ? "14px" : "16px",
  };

  const activityItemStyle: React.CSSProperties = {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    padding: isMobile ? "10px 0" : "12px 0",
    borderBottom: "1px solid #f3f4f6",
    gap: "12px",
  };

  const patientInfoStyle: React.CSSProperties = {
    display: "grid",
    gridTemplateColumns: isMobile
      ? "1fr"
      : isTablet
      ? "repeat(2, 1fr)"
      : "repeat(auto-fit, minmax(200px, 1fr))",
    gap: isMobile ? "12px" : "16px",
    fontSize: isMobile ? "14px" : "16px",
  };

  return (
    <div style={containerStyle}>
      <style>
        {`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
          
          /* Improve text readability on small screens */
          @media (max-width: 480px) {
            .patient-info-item {
              font-size: 13px !important;
            }
            .stat-value {
              font-size: 16px !important;
              font-weight: 600 !important;
            }
          }
        `}
      </style>

      {/* Patient Info Header */}
      <div style={{ ...cardStyle, marginBottom: isMobile ? "16px" : "24px" }}>
        <div style={patientInfoStyle}>
          <div className="patient-info-item">
            <strong>📞 Phone:</strong> {patient.phone}
          </div>
          <div className="patient-info-item">
            <strong>✉️ Email:</strong> {patient.email || "Not provided"}
          </div>
          <div className="patient-info-item">
            <strong>🩸 Blood Type:</strong> {patient.blood_type || "Unknown"}
          </div>
          <div className="patient-info-item">
            <strong>🏥 Clinic:</strong> {patient.clinic?.name || "Not assigned"}
          </div>
          <div className="patient-info-item">
            <strong>👨‍⚕️ Primary Doctor:</strong>{" "}
            {patient.primary_doctor?.name || "Not assigned"}
          </div>
          <div className="patient-info-item">
            <strong>🛡️ Insurance:</strong>{" "}
            {patient.insurance_provider || "No insurance"}
          </div>
        </div>

        <div
          style={{
            marginTop: isMobile ? "12px" : "16px",
            paddingTop: isMobile ? "12px" : "16px",
            borderTop: "1px solid #f3f4f6",
            fontSize: isMobile ? "14px" : "16px",
          }}
        >
          <strong>🏠 Address:</strong> {patient.address}
        </div>

        {patient.emergency_contact && (
          <div
            style={{
              marginTop: "12px",
              fontSize: isMobile ? "14px" : "16px",
            }}
          >
            <strong>🚨 Emergency Contact:</strong>{" "}
            {patient.emergency_contact.name} (
            {patient.emergency_contact.relationship}) -{" "}
            {patient.emergency_contact.phone}
          </div>
        )}
      </div>

      <div style={gridStyle}>
        {/* Alerts */}
        {overviewData && overviewData.alerts.length > 0 && (
          <div style={cardStyle}>
            <h3 style={cardTitleStyle}>⚠️ Alerts & Warnings</h3>
            {overviewData.alerts.map((alert, index) => (
              <div key={index} style={alertStyle(alert.type)}>
                <div style={{ fontWeight: "500" }}>{alert.message}</div>
                {alert.details && (
                  <div
                    style={{
                      fontSize: isMobile ? "11px" : "12px",
                      marginTop: "4px",
                      opacity: "0.9",
                    }}
                  >
                    {alert.details.join(", ")}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        {/* Quick Stats */}
        {overviewData && (
          <div style={cardStyle}>
            <h3 style={cardTitleStyle}>📊 Quick Statistics</h3>
            <div style={statItemStyle}>
              <span>Total Visits</span>
              <strong className="stat-value">
                {overviewData.quickStats.total_visits}
              </strong>
            </div>
            <div style={statItemStyle}>
              <span>Upcoming Visits</span>
              <strong className="stat-value">
                {overviewData.quickStats.upcoming_visits}
              </strong>
            </div>
            <div style={statItemStyle}>
              <span>Pending Lab Results</span>
              <strong className="stat-value">
                {overviewData.quickStats.pending_labs}
              </strong>
            </div>
            <div style={statItemStyle}>
              <span>Active Treatments</span>
              <strong className="stat-value">
                {overviewData.quickStats.active_treatments}
              </strong>
            </div>
            {overviewData.quickStats.last_visit_date && (
              <div style={statItemStyle}>
                <span>Last Visit</span>
                <strong className="stat-value">
                  {PatientOverviewService.formatDate(
                    overviewData.quickStats.last_visit_date
                  )}
                </strong>
              </div>
            )}
            {overviewData.quickStats.next_visit_date && (
              <div style={statItemStyle}>
                <span>Next Visit</span>
                <strong className="stat-value">
                  {PatientOverviewService.formatDate(
                    overviewData.quickStats.next_visit_date
                  )}
                </strong>
              </div>
            )}
          </div>
        )}

        {/* Recent Activity */}
        {overviewData && (
          <div style={cardStyle}>
            <h3 style={cardTitleStyle}>🕒 Recent Activity</h3>
            {overviewData.recentActivity.length === 0 ? (
              <p
                style={{
                  color: "#6b7280",
                  textAlign: "center",
                  padding: "20px",
                  fontSize: isMobile ? "14px" : "16px",
                }}
              >
                No recent activity
              </p>
            ) : (
              overviewData.recentActivity.map((activity) => (
                <div key={activity.id} style={activityItemStyle}>
                  <div
                    style={{
                      display: "flex",
                      alignItems: "flex-start",
                      gap: isMobile ? "8px" : "12px",
                      flex: 1,
                      minWidth: 0,
                    }}
                  >
                    <span
                      style={{
                        fontSize: isMobile ? "16px" : "20px",
                        flexShrink: 0,
                      }}
                    >
                      {PatientOverviewService.getActivityIcon(activity.type)}
                    </span>
                    <div style={{ minWidth: 0, flex: 1 }}>
                      <div
                        style={{
                          fontWeight: "500",
                          fontSize: isMobile ? "14px" : "16px",
                          lineHeight: "1.3",
                        }}
                      >
                        {activity.title}
                      </div>
                      <div
                        style={{
                          fontSize: isMobile ? "11px" : "12px",
                          color: "#6b7280",
                          marginTop: "2px",
                        }}
                      >
                        {PatientOverviewService.formatDate(activity.date)}
                        {activity.details?.doctor && (
                          <span> • Dr. {activity.details.doctor}</span>
                        )}
                      </div>
                      {activity.details?.services &&
                        activity.details.services.length > 0 && (
                          <div
                            style={{
                              fontSize: isMobile ? "10px" : "11px",
                              color: "#9ca3af",
                              marginTop: "2px",
                            }}
                          >
                            {activity.details.services.slice(0, 2).join(", ")}
                            {activity.details.services.length > 2 && " +more"}
                          </div>
                        )}
                    </div>
                  </div>
                  <span
                    style={{
                      padding: isMobile ? "3px 6px" : "4px 8px",
                      borderRadius: "12px",
                      fontSize: isMobile ? "10px" : "11px",
                      fontWeight: "500",
                      backgroundColor:
                        PatientOverviewService.getStatusColor(activity.status) +
                        "20",
                      color: PatientOverviewService.getStatusColor(
                        activity.status
                      ),
                      textTransform: "capitalize",
                      whiteSpace: "nowrap",
                      flexShrink: 0,
                    }}
                  >
                    {activity.status}
                  </span>
                </div>
              ))
            )}
          </div>
        )}

        {/* Medical Information */}
        <div style={cardStyle}>
          <h3 style={cardTitleStyle}>🏥 Medical Information</h3>
          {patient.medical_notes ? (
            <div style={{ marginBottom: isMobile ? "12px" : "16px" }}>
              <strong style={{ fontSize: isMobile ? "14px" : "16px" }}>
                Medical Notes:
              </strong>
              <p
                style={{
                  marginTop: "8px",
                  color: "#4b5563",
                  lineHeight: "1.5",
                  fontSize: isMobile ? "14px" : "16px",
                }}
              >
                {patient.medical_notes}
              </p>
            </div>
          ) : (
            <p
              style={{
                color: "#6b7280",
                fontStyle: "italic",
                fontSize: isMobile ? "14px" : "16px",
                textAlign: "center",
                padding: isMobile ? "16px" : "20px",
              }}
            >
              No medical notes recorded
            </p>
          )}

          {/* Add refresh button */}
          <div
            style={{
              marginTop: isMobile ? "12px" : "16px",
              paddingTop: isMobile ? "12px" : "16px",
              borderTop: "1px solid #f3f4f6",
              textAlign: "right",
            }}
          >
            <button
              onClick={loadOverviewData}
              style={{
                padding: "6px 12px",
                backgroundColor: "#f3f4f6",
                color: "#374151",
                border: "1px solid #d1d5db",
                borderRadius: "6px",
                cursor: "pointer",
                fontSize: isMobile ? "12px" : "14px",
                display: "flex",
                alignItems: "center",
                gap: "6px",
                marginLeft: "auto",
              }}
            >
              🔄 Refresh
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PatientOverviewTab;
